import produce from 'immer'

const prefix = 'TIMER/'

let timerTimeout

const generateAction = ({
    baseActionCreatorName,
    baseActionName,
    prefix: actionTypePrefix = '',
    handleAction,
}) => {
    const actionTypesMap = {
        base: `${actionTypePrefix}${baseActionName}`,
        success: `${actionTypePrefix}${baseActionName}_SUCCESS`,
        error: `${actionTypePrefix}${baseActionName}_ERROR`,
    }

    const actionCreatorsMap = {
        base: baseActionCreatorName,
        success: `${baseActionCreatorName}Success`,
        error: `${baseActionCreatorName}Error`,
    }

    const actions = {
        baseAction: {
            type: actionTypesMap.base,
            actionCreatorName: actionCreatorsMap.base,
            creator: () => ({ payload } = {}) => ({
                type: actionTypesMap.base,
                ...payload,
            }),
        },
        successAction: {
            type: actionTypesMap.success,
            actionCreatorName: actionCreatorsMap.success,
            creator: () => ({ payload } = {}) => ({
                type: actionTypesMap.success,
                payload,
            }),
        },
        errorAction: {
            type: actionTypesMap.error,
            actionCreatorName: actionCreatorsMap.error,
            creator: () => ({ payload, error } = {}) => ({
                type: actionTypesMap.error,
                payload,
                error,
            }),
        },
    }

    const actionsArray = Object.values(actions)

    const types = actionsArray.map(({ type }) => type)

    const creators = () => actionsArray.reduce((acc, { creator, actionCreatorName }) => {
        /* eslint-disable-next-line no-param-reassign */
        acc[actionCreatorName] = creator()
        return acc
    }, {})

    return {
        types,
        creators,
        handleAction,
        ...actionTypesMap,
    }
}

const set = generateAction({
    prefix,
    baseActionCreatorName: 'set',
    baseActionName: 'SET',
    handleAction: (
        { duration, onDoneAction } = {},
        { dispatch, getState } = {},
        Timer,
    ) => {
        try {
            const state = getState()
            const isRunning = Timer.selectors.isRunning(state)

            if (isRunning) {
                throw new Error('cannot set timer, it must be stopped first')
            }

            if (!duration || !Number.isInteger(duration)) {
                throw new Error('duration must be declared as an integer')
            }

            if (!onDoneAction || !onDoneAction?.type) {
                throw new Error('onDoneAction must be declared with a type')
            }

            dispatch({
                type: set.success,
                payload: {
                    duration,
                    onDoneAction,
                },
            })
        } catch (error) {
            dispatch({
                type: set.error,
                error: error.message,
            })
        }
    },
})

const start = generateAction({
    prefix,
    baseActionCreatorName: 'start',
    baseActionName: 'START',
    handleAction: (
        action,
        { dispatch, getState } = {},
        Timer,
    ) => {
        try {
            const state = getState()
            const isRunning = Timer.selectors.isRunning(state)

            if (isRunning) {
                throw new Error('cannot start timer, already running')
            }

            const duration = Timer.selectors.duration(state)
            const onDoneAction = Timer.selectors.onDoneAction(state)
            const elapsed = Timer.selectors.elapsed(state) || 0

            if (!duration || !onDoneAction) {
                throw new Error('timer has not been set')
            }

            const startTime = Date.now()

            timerTimeout = setTimeout(() => {
                dispatch(Timer.creators.stop())
                dispatch(onDoneAction)
            }, duration - elapsed)

            dispatch({
                type: start.success,
                payload: {
                    startTime,
                },
            })
        } catch (error) {
            dispatch({
                type: start.error,
                error: error.message,
            })
        }
    },
})

const stop = generateAction({
    prefix,
    baseActionCreatorName: 'stop',
    baseActionName: 'STOP',
    handleAction: (
        action,
        { dispatch, getState } = {},
        Timer,
    ) => {
        try {
            const state = getState()
            const isRunning = Timer.selectors.isRunning(state)

            if (!isRunning) {
                throw new Error('cannot stop timer, not running')
            }

            clearTimeout(timerTimeout)

            const elapsedTime = Timer.selectors.elapsed(state)
            const startTime = Timer.selectors.startTime(state)

            const stopTime = Date.now()

            const elapsed = (stopTime - startTime) + elapsedTime

            dispatch({
                type: stop.success,
                payload: {
                    elapsed,
                },
            })
        } catch (error) {
            dispatch({
                type: stop.error,
                error: error.message,
            })
        }
    },
})

const TimerFactory = ({
    reducerName = `timer_middleware_${Date.now()}`,
} = {}) => {
    const Timer = {
        reducerName,
        types: {
            ...set.types,
            ...start.types,
            ...stop.types,
        },
        creators: {
            ...set.creators(),
            ...start.creators(),
            ...stop.creators(),
        },
        initialState: {
            startTime: null,
            isRunning: false,
            duration: null,
            onDoneAction: null,
            elapsed: 0,
        },
        reducer: (
            state = Timer.initialState,
            { type, payload, error } = {},
        ) => produce(state, (draftState) => {
            switch (type) {
                case set.base:
                    break

                case set.success:
                    draftState.duration = payload.duration
                    draftState.onDoneAction = payload.onDoneAction
                    draftState.elapsed = Timer.initialState.elapsed
                    draftState.startTime = Timer.initialState.startTime
                    break

                case set.error:
                    draftState.error = error || 'set timer error occured'
                    break

                case start.base:
                    break

                case start.success:
                    draftState.isRunning = true
                    draftState.startTime = payload.startTime
                    break

                case start.error:
                    draftState.error = error || 'start timer error occured'
                    break

                case stop.base:
                    break

                case stop.success:
                    draftState.isRunning = false
                    draftState.elapsed = payload.elapsed
                    break

                case stop.error:
                    draftState.error = error || 'stop timer error occured'
                    break

                default:
                    // do nothing
            }
        }),
        selectors: {
            timer: (state) => state?.[Timer.reducerName],
            startTime: (state) => Timer.selectors.timer(state)?.startTime,
            duration: (state) => Timer.selectors.timer(state)?.duration,
            elapsed: (state) => Timer.selectors.timer(state)?.elapsed,
            isRunning: (state) => Timer.selectors.timer(state)?.isRunning,
            onDoneAction: (state) => Timer.selectors.timer(state)?.onDoneAction,
            timeRemaining: (state) => (
                Timer.selectors.duration(state) - Timer.selectors.startTime(state)
            ),
        },
        handleAction: (action, storeAPI) => {
            switch (action?.type) {
                case set.base:
                    set.handleAction(action, storeAPI, Timer)
                    break

                case start.base:
                    start.handleAction(action, storeAPI, Timer)
                    break

                case stop.base:
                    stop.handleAction(action, storeAPI, Timer)
                    break
                default:
                    // do nothing
            }
        },
        mapStateToProps: (state) => ({
            isSubmitting: Timer.selectors.isSubmitting(state),
        }),
        mapDispatchToProps: (dispatch) => ({
            setIsSubmitting: (payload) => dispatch(Timer.creators.setIsSubmitting(payload)),
        }),
    }

    return Timer
}

export default TimerFactory
